import { requestCodexText } from './codexText';
import { Character2DLayered, CharacterPassport, CharacterArchetype } from '../types';
import { generateImage } from './imageGeneration';
import { uploadTempCharacterImage } from './character2dService';
import { SFW_UNDERWEAR, SFW_UNDERWEAR_CONSERVATIVE, getUnderwearFromCensorshipType, isPhotographicStyle } from './layerPrompts';
import { isContentModerationError } from './generationErrors';
import { CensorshipType } from './geminiCharacterAnalysis';
import { getStyleById } from './characterStyles';

// ============================================
// SYSTEM PROMPTS
// ============================================

const PASSPORT_TEXT_SYSTEM_PROMPT = `You are an expert character writer and lore master.
YOUR TASK: Analyze the provided visual descriptions of a character's layers (Body, Face, Hair) and synthesize them into a canonical text description and passport.

OUTPUT FORMAT (JSON):
{
  "name": "Suggested Name (if none provided)",
  "short_description": "2-sentence summary of appearance and vibe.",
  "full_description": "Detailed 2-paragraph physical description incorporating all visual elements naturally.",
  "personality_hint": "Inferred personality traits based on visual cues (e.g., 'stoic', 'flamboyant', 'mysterious').",
  "keywords": ["tag1", "tag2", "tag3"]
}

RULES:
- Be descriptive and evocative.
- Merge the disparate layer prompts into a cohesive whole.
- Ignore technical prompt terms like "grey background", "best quality", "8k". Focus on the subject.
- Maintain the style/archetype vibe (e.g., if "Cyberpunk", use tech noir language).
`;

// ============================================
// TYPES
// ============================================

interface PassportGenerationResult {
    textData: {
        name?: string;
        short_description: string;
        full_description: string;
        personality_hint: string;
        keywords: string[];
    };
    referenceSheetUrl: string;
    usage_prompt: string;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get the appropriate style string for a character based on archetype and style_preset
 * Returns both the base archetype style AND specific substyle keywords
 */
function getCharacterStyleString(character: Character2DLayered): string {
    // Base archetype styles
    const archetypeStyles: Record<string, string> = {
        'manga_anime': 'anime, manga, anime style, manga style, masterpiece, full color, 2D anime manga illustration',
        'comic_cartoon': 'comic book style, western graphic novel, sharp ink lines, digital coloring, vibrant colors',
        'game_3d': '3D game character, stylized realism, game-ready, video game quality, PBR textures',
        'film': 'photorealistic, cinematic quality, professional lighting, film photography, detailed textures'
    };

    // Get base archetype style
    const archetype = character.archetype || 'film';
    const baseStyle = archetypeStyles[archetype] || 'Professional Character Concept Art';

    // Get specific substyle keywords if style_preset is set
    if (character.style_preset && character.archetype) {
        const style = getStyleById(character.archetype as CharacterArchetype, character.style_preset);
        if (style?.promptKeywords && style.promptKeywords.length > 0) {
            // Use first 5 keywords from the specific substyle
            const substyleKeywords = style.promptKeywords.slice(0, 5).join(', ');
            console.log('📋 Using style for reference sheet:', style.name, '-', substyleKeywords);
            return `${baseStyle}, ${substyleKeywords}`;
        }
    }

    return baseStyle;
}

// ============================================
// SERVICE
// ============================================

export async function generateCharacterPassport(
    character: Character2DLayered
): Promise<PassportGenerationResult> {
    // 1. Generate Text Description (Passport)
    const textData = await generatePassportText(character);

    // 2. Generate Visual Reference Sheet
    const referenceSheetUrl = await generateReferenceSheet(character, textData.full_description);

    // 3. Construct the "Magic Prompt" for future usage
    // Make reference to the attached image very explicit for better character consistency
    const usage_prompt = `IMPORTANT: Use the ATTACHED REFERENCE SHEET IMAGE as the primary visual guide. The character in the reference sheet image must be recreated with EXACT visual fidelity - same face, same hair, same body type, same proportions. ${textData.full_description} [USER_PROMPT_PLACEHOLDER] CRITICAL: Match the character's appearance pixel-perfectly to the attached reference sheet image.`;

    return {
        textData,
        referenceSheetUrl,
        usage_prompt
    };
}

// ============================================
// WARDROBE PASSPORT (Character + Outfit)
// ============================================

export async function generateWardrobePassport(
    character: Character2DLayered
): Promise<PassportGenerationResult> {
    // 1. Generate Wardrobe Text (updated description)
    const textData = await generatePassportText(character); // Re-run with outfit layers included

    // 2. Generate Wardrobe Reference Sheet
    const referenceSheetUrl = await generateWardrobeReferenceSheet(character, textData.full_description);

    // 3. Wardrobe specific usage prompt
    const usage_prompt = `Generate an image of the character shown in the attached wardrobe sheet. ${textData.full_description} wearing the outfit shown. [USER_PROMPT_PLACEHOLDER]`;

    return {
        textData,
        referenceSheetUrl,
        usage_prompt
    };
}

/**
 * Use the private Codex connection to generate the text passport
 */
async function generatePassportText(character: Character2DLayered) {
    // Collect prompts from layers
    const prompts = [
        `Archetype: ${character.style_preset || 'General'}`,
        `Body: ${character.core_layers.body?.prompt || 'Standard body'}`,
        `Head (face & hair): ${character.core_layers.head?.prompt_used || 'Standard head'}`,
        character.core_layers.clothing ? `Outfit: ${character.core_layers.clothing.prompt_used}` : '',
        character.core_layers.accessories && character.core_layers.accessories.length > 0
            ? `Accessories: ${character.core_layers.accessories.map(a => a.prompt_used).join(', ')}`
            : ''
    ].filter(Boolean).join('\n');

    const userPrompt = `CHARACTER NAME: ${character.name}\n\nVISUAL TRAITS:\n${prompts}`;

    try {
        const resultText = await requestCodexText(PASSPORT_TEXT_SYSTEM_PROMPT + '\n\n' + userPrompt);

        return JSON.parse(resultText);

    } catch (error) {
        console.error('Failed to generate passport text:', error);
        // Fallback
        return {
            name: character.name,
            short_description: 'A custom character.',
            full_description: `A unique character with specific visual traits designed by the user.`,
            personality_hint: 'Unknown',
            keywords: ['custom']
        };
    }
}

/**
 * Call Image Generation Service to create the Reference Sheet
 * Uses template image, body layer, and head layer as references
 */
async function generateReferenceSheet(
    character: Character2DLayered,
    fullDescription: string
): Promise<string> {
    const bodyUrl = character.core_layers.body?.rendered_url;
    const headUrl = character.core_layers.head?.rendered_url;

    console.log('📋 Passport reference sheet - Available layers:', {
        body: bodyUrl ? '✓' : '✗',
        head: headUrl ? '✓' : '✗',
        style_preset: character.style_preset,
        archetype: character.archetype
    });

    // Use the fullDescription from generatePassportText (already synthesized body+head)
    // No need for a separate Gemini call - eliminates redundant API overhead
    const combinedDescription = fullDescription;

    // Template for reference sheet layout structure
    const TEMPLATE_URL = new URL('/templates/character-template-borderless.png', window.location.origin).href;

    // Determine the correct style string based on archetype or style_preset
    const styleString = getCharacterStyleString(character);
    console.log('📋 Using style for reference sheet:', styleString);

    // Get the appropriate underwear based on form and detected gender
    // Creatures without visible anatomy (neutral gender) don't need coverage
    const bodyGender = character.core_layers.body?.gender || 'neutral';
    const isCreature = character.form === 'creature';

    // Determine censorship type based on form and gender
    let censorshipType: CensorshipType = 'creature_uncensored'; // default for neutral creatures
    if (isCreature) {
        if (bodyGender === 'female') censorshipType = 'creature_female_censored';
        else if (bodyGender === 'male') censorshipType = 'creature_male_censored';
        else censorshipType = 'creature_uncensored'; // neutral = no visible anatomy
    } else {
        // Humanoid
        if (bodyGender === 'female') censorshipType = 'female_censored';
        else censorshipType = 'male_censored'; // male or neutral humanoids get shorts
    }

    // Helper to get underwear prompt - normal or conservative based on retry
    const getUnderwearPromptForAttempt = (useConservative: boolean): string => {
        if (!isCreature && isPhotographicStyle(styleString)) {
            return 'the same opaque athletic base-layer garments shown in the BODY reference image; preserve their fabric, cut and coverage in all views';
        }
        if (isCreature && censorshipType === 'creature_uncensored') {
            return ''; // No coverage needed for neutral creatures
        }
        if (useConservative && bodyGender === 'female') {
            return SFW_UNDERWEAR_CONSERVATIVE.female;
        }
        return getUnderwearFromCensorshipType(censorshipType);
    };

    // Helper to build prompt with optional conservative underwear
    const buildRefSheetPrompt = (useConservative: boolean): string => {
        const underwearPrompt = getUnderwearPromptForAttempt(useConservative);

        // Preserve character details while composing the reference views:
        // "Using the provided images, place [element from image 2] onto [element from image 1].
        //  Ensure that the features of [element from image 1] remain completely unchanged."
        return `Create a detailed Character Reference Sheet using the provided reference images.

Using the provided images:
- IMAGE 1 (FIRST): The reference sheet LAYOUT TEMPLATE showing zone positions ONLY (use for composition, NOT content)
- IMAGE 2 (SECOND): The character's BODY (full figure with blank/featureless head)
- IMAGE 3 (THIRD): The character's HEAD AND FACE (face, hair, and facial features)

⚠️ CRITICAL - TEMPLATE USAGE:
IMAGE 1 is ONLY a layout guide showing WHERE to place each view. DO NOT copy any visual elements from the template:
- DO NOT draw wireframes, construction lines, or guide lines on any face or body
- DO NOT copy the skeletal/anatomical overlay shown in the template
- DO NOT add any grid lines, topology lines, or mesh patterns
- The template shows placeholder figures - replace them entirely with the actual character
- All faces and bodies must be CLEAN, fully rendered artwork with NO overlay graphics

Place the HEAD from IMAGE 3 onto the BODY from IMAGE 2, then render this combined character in each zone position.

Ensure that the features of IMAGE 2 (the body) remain completely unchanged:
- Exact same body shape, proportions, and physique
- Exact same muscle definition and body type
- Exact same skin tone and texture
- Exact same height and limb proportions

Ensure that the features of IMAGE 3 (the head) remain completely unchanged:
- Exact same facial structure - eyes, nose, mouth, chin, cheeks
- Exact same hairstyle - length, texture, color, volume, style
- Exact same skin complexion and facial proportions
- Exact same expression and features

The head should integrate naturally with the body where they connect, matching skin tone at the neck seamlessly.

⚠️ POSE REQUIREMENT (CRITICAL):
- Use a NATURAL RELAXED STANDING POSE - arms hanging naturally at sides, NOT spread out
- DO NOT use A-pose (arms at 45 degrees) or T-pose (arms horizontal)
- Match the natural, relaxed pose shown in IMAGE 2 (the body reference)
- Arms should hang loosely by the sides of the body, slightly bent at elbows
- This applies to ALL full body views in the reference sheet

${underwearPrompt ? `CLOTHING: The character MUST be wearing ${underwearPrompt}. This is required for all body views.` : 'CLOTHING: The character should appear exactly as shown in the body reference image - no added clothing or coverage needed.'}

LAYOUT (5 zones - positions from IMAGE 1 template):
- Zone 1 (Far Left): Full Body Front View - natural relaxed pose, arms at sides
- Zone 2 (Center Left): Full Body 3/4 Angle - same relaxed pose from angle
- Zone 3 (Top Right): CLEAN extreme close-up of face (eyes, nose, mouth) - NO wireframes or guide lines
- Zone 4 (Bottom Right Left): CLEAN headshot front view - NO wireframes or guide lines
- Zone 5 (Bottom Right Right): CLEAN headshot 3/4 angle - NO wireframes or guide lines

CHARACTER DESCRIPTION: ${combinedDescription}

STYLE: ${styleString}
LIGHTING: Flat, even studio lighting
BACKGROUND: Neutral grey
OUTPUT: Professional character reference sheet with clean, fully-rendered artwork. NO outer rectangular frame, perimeter border, or boxed outside margin. Keep the five-view arrangement; only internal panel dividers may separate the views. NO text, watermarks, labels, wireframes, or construction lines anywhere.`;
    };

    // Collect all reference images: template + body + head
    const referenceImages: string[] = [TEMPLATE_URL]; // Template always first
    if (bodyUrl) referenceImages.push(bodyUrl);
    if (headUrl) referenceImages.push(headUrl);

    console.log('📋 Using reference images (template + body + head):', referenceImages);
    console.log('📋 Combined description:', combinedDescription);

    // Retry logic for reference sheet generation with conservative underwear fallback
    const MAX_RETRIES = 2;
    let lastError: Error | null = null;
    let useConservativeUnderwear = false;
    let imageGenerated = false;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
            const promptToUse = buildRefSheetPrompt(useConservativeUnderwear);
            if (useConservativeUnderwear) {
                console.log(`📋 Reference sheet generation attempt ${attempt}/${MAX_RETRIES} (with conservative underwear: sports bra + yoga shorts)`);
            } else {
                console.log(`📋 Reference sheet generation attempt ${attempt}/${MAX_RETRIES}`);
            }

            const result = await generateImage({
                prompt: promptToUse,
                model: 'openai/gpt-image-2.5-sunburst',
                aspectRatio: '3:2',
                resolution: 'High',
                // Pass all three reference images: template, body, head
                characterReference: referenceImages.length > 0 ? {
                    id: character.id,
                    name: character.name || 'Character',
                    snapshotUrl: referenceImages[0],
                    snapshotUrls: referenceImages,
                    metadata: {}
                } : undefined
            });

            imageGenerated = Boolean(result.imageBlob || result.imageUrl);
            if (result.imageBlob) {
                // Upload to R2 Temp Storage (will be moved to final when character is finalized)
                const filename = `refsheet_${character.id}_${Date.now()}.png`;
                const url = await uploadTempCharacterImage(
                    result.imageBlob,
                    character.user_id,
                    character.project_id,
                    character.id,
                    filename
                );
                console.log(`✅ Reference sheet generated successfully on attempt ${attempt} (saved to temp)`);
                return url;
            } else if (result.imageUrl) {
                // If we got a URL (e.g. from Pollinations), we should download and re-upload to persist it
                const resp = await fetch(result.imageUrl);
                const blob = await resp.blob();
                const filename = `refsheet_${character.id}_${Date.now()}.png`;
                const url = await uploadTempCharacterImage(
                    blob,
                    character.user_id,
                    character.project_id,
                    character.id,
                    filename
                );
                console.log(`✅ Reference sheet generated successfully on attempt ${attempt} (saved to temp)`);
                return url;
            }

            throw new Error('No image generated for reference sheet');

        } catch (error) {
            lastError = error instanceof Error ? error : new Error(String(error));
            console.error(`❌ Reference sheet attempt ${attempt}/${MAX_RETRIES} failed:`, error);

            // Only retry an explicit moderation rejection with a safer prompt.
            // Timeouts and upload failures may follow a completed, charged generation.
            const isContentModeration = isContentModerationError(lastError);
            if (imageGenerated || !isContentModeration || bodyGender !== 'female' || useConservativeUnderwear || isPhotographicStyle(styleString)) {
                throw lastError;
            }
            useConservativeUnderwear = true;

            if (attempt < MAX_RETRIES) {
                // Pause before the single safer-prompt retry.
                const delay = Math.pow(2, attempt) * 1000;
                console.log(`🔄 Retrying in ${delay}ms...`);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
    }

    // All retries exhausted - throw error so caller knows generation failed
    console.error(`❌ Reference sheet generation failed after ${MAX_RETRIES} attempts`);
    throw lastError || new Error('Reference sheet generation failed after all retries');
}

/**
 * Generate Wardrobe Reference Sheet
 * specifically saving to generations/character/wardrobe
 */
async function generateWardrobeReferenceSheet(
    character: Character2DLayered,
    fullDescription: string
): Promise<string> {
    // Determine the correct style string based on archetype or style_preset
    const styleString = getCharacterStyleString(character);
    console.log('📋 Using style for wardrobe sheet:', styleString);

    // Gemini-optimized wardrobe prompt using HIGH-FIDELITY DETAIL PRESERVATION format
    const WARDROBE_PROMPT = `Create a professional Character Wardrobe Reference Sheet using the provided reference images.

Using the provided images:
- IMAGE 1 (FIRST): The reference sheet LAYOUT TEMPLATE showing zone positions ONLY (use for composition, NOT content)
- IMAGE 2 (SECOND): The character's BODY (full figure)
- IMAGE 3 (THIRD): The character's HEAD AND FACE (face and hair)

⚠️ CRITICAL - TEMPLATE USAGE:
IMAGE 1 is ONLY a layout guide showing WHERE to place each view. DO NOT copy any visual elements from the template:
- DO NOT draw wireframes, construction lines, or guide lines on any face or body
- DO NOT copy the skeletal/anatomical overlay shown in the template
- DO NOT add any grid lines, topology lines, or mesh patterns
- The template shows placeholder figures - replace them entirely with the actual character
- All faces and bodies must be CLEAN, fully rendered artwork with NO overlay graphics

Place the HEAD from IMAGE 3 onto the BODY from IMAGE 2, dress this combined character in the specified outfit, then render in each zone position.

Ensure that the features of IMAGE 2 (the body) remain completely unchanged:
- Exact same body shape, proportions, and physique
- Exact same muscle definition and body type
- Exact same skin tone and texture

Ensure that the features of IMAGE 3 (the head) remain completely unchanged:
- Exact same facial structure - eyes, nose, mouth, chin, cheeks
- Exact same hairstyle - length, texture, color, volume, style
- Exact same skin complexion and facial proportions

The head should integrate naturally with the body, matching skin tone at the neck seamlessly.

LAYOUT:
- Format: 3:2 Aspect Ratio (Horizontal)
- Background: Neutral studio background
- Content:
  1. Full Body Front View showing the COMPLETE OUTFIT - CLEAN rendering, no wireframes
  2. Side/Back View showing outfit details - CLEAN rendering, no wireframes
  3. Detail shots of accessories

CHARACTER & OUTFIT: ${fullDescription}

STYLE: ${styleString}
OUTPUT: Professional wardrobe reference with clean, fully-rendered artwork. NO outer rectangular frame, perimeter border, or boxed outside margin. NO wireframes, construction lines, or overlay graphics.
Focus on fabric textures, accessory details, and overall look.`;

    // Template for wardrobe sheet layout
    const TEMPLATE_URL = new URL('/templates/character-template-borderless.png', window.location.origin).href;

    // Collect all reference images: template + body + head
    const bodyUrl = character.core_layers.body?.rendered_url;
    const headUrl = character.core_layers.head?.rendered_url;

    const referenceImages: string[] = [TEMPLATE_URL]; // Template always first
    if (bodyUrl) referenceImages.push(bodyUrl);
    if (headUrl) referenceImages.push(headUrl);

    console.log('📋 Wardrobe sheet - Using reference images (template + layers):', referenceImages);

    try {
        const result = await generateImage({
            prompt: WARDROBE_PROMPT,
            model: 'openai/gpt-image-2.5-sunburst',
            aspectRatio: '3:2',
            resolution: 'High',
            characterReference: referenceImages.length > 0 ? {
                id: character.id,
                name: character.name || 'Character',
                snapshotUrl: referenceImages[0],
                snapshotUrls: referenceImages,
                metadata: {}
            } : undefined
        });

        if (result.imageBlob) {
            const filename = `wardrobe_sheet_${character.id}_${Date.now()}.png`;
            // Upload to 'generations/character/wardrobe' path
            // We need to construct the path manually for uploadPermanentCharacterImage or use a raw upload
            // uploadPermanentCharacterImage puts it in `generations/character/${userId}/${filename}`
            // The user requested `generations/character/wardrobe`
            // Let's modify uploadPermanentCharacterImage or allow custom path?
            // checking uploadPermanentCharacterImage signature in character2dService.ts:
            // export async function uploadPermanentCharacterImage(blob: Blob, userId: string, filename: string): Promise<string>
            // It constructs the key internally.

            // Hack: we prefix the filename with 'wardrobe/' ?
            // No, that might break if it sanitizes.
            // Let's assume we can pass a 'folder' arg to a generic upload function? 
            // Looking at viewed code memory... character2dService.ts: uploadCharacterImageToR2 is the generic one.

            // We need to import uploadCharacterImageToR2
            const { uploadCharacterImageToR2 } = await import('./character2dService');

            const folder = `generations/character/wardrobe/${character.user_id}/${character.id}`;
            const url = await uploadCharacterImageToR2(result.imageBlob, character.user_id, filename, folder);
            return url;
        } else if (result.imageUrl) {
            const { uploadCharacterImageToR2 } = await import('./character2dService');
            const resp = await fetch(result.imageUrl);
            const blob = await resp.blob();
            const filename = `wardrobe_sheet_${character.id}_${Date.now()}.png`;
            const folder = `generations/character/wardrobe/${character.user_id}/${character.id}`;
            const url = await uploadCharacterImageToR2(blob, character.user_id, filename, folder);
            return url;
        }

        throw new Error('No image generated for wardrobe sheet');

    } catch (error) {
        console.error('Failed to generate wardrobe sheet:', error);
        return 'https://placehold.co/1200x800/222/999?text=Wardrobe+Sheet+Generation+Failed';
    }
}
